<script lang="ts" setup>
import { reactive, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useFeatureFlagsStore, useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import type { FeatureFlagType } from '@/types/featureFlags'

const { t } = useI18n()

defineProps<{
  loading?: boolean
}>()

const emits = defineEmits<{
  (e: 'edit', flag: FeatureFlagType): void
  (e: 'refresh'): void
}>()

const featureFlagsStore = useFeatureFlagsStore()
const notifyStore = useNotifyStore()
const { adminFlagsGrouped, adminCategories, adminFlags } = storeToRefs(featureFlagsStore)

const data = reactive({
  search: '',
  expandedCategories: new Set<string>(),
  togglingFlag: null as string | null,
  deletingFlag: null as string | null
})

// Initialize all categories as expanded
const initExpandedCategories = () => {
  adminCategories.value.forEach((cat) => {
    data.expandedCategories.add(cat)
  })
}
initExpandedCategories()

// Filter flags based on search
const filteredFlagsGrouped = computed(() => {
  if (!data.search.trim()) {
    return adminFlagsGrouped.value
  }

  const search = data.search.toLowerCase()
  const result: Record<string, FeatureFlagType[]> = {}

  for (const [category, flags] of Object.entries(adminFlagsGrouped.value)) {
    const filtered = flags.filter(
      (f) =>
        f.name.toLowerCase().includes(search) ||
        f.category.toLowerCase().includes(search)
    )
    if (filtered.length > 0) {
      result[category] = filtered
    }
  }

  return result
})

const filteredCategories = computed(() => Object.keys(filteredFlagsGrouped.value).sort())

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const totalFilteredFlags = computed(() => {
  return Object.values(filteredFlagsGrouped.value).reduce((acc, flags) => acc + flags.length, 0)
})

// Toggle category expansion
const toggleCategory = (category: string) => {
  if (data.expandedCategories.has(category)) {
    data.expandedCategories.delete(category)
  } else {
    data.expandedCategories.add(category)
  }
}

const isCategoryExpanded = (category: string) => data.expandedCategories.has(category)

// Get unique key for a flag
const getFlagKey = (flag: FeatureFlagType) => `${flag.category}:${flag.name}`

// Toggle flag enabled state
const handleToggle = async (flag: FeatureFlagType, event: Event) => {
  event.stopPropagation()
  const key = getFlagKey(flag)
  if (data.togglingFlag) return

  data.togglingFlag = key

  const success = await featureFlagsStore.toggleFlag(flag.category, flag.name)

  if (success) {
    notifyStore.createNotify({
      id: uuid(),
      title: t('common.success'),
      message: t(!flag.enabled ? 'dashboard.admin.features.notifications.enabled' : 'dashboard.admin.features.notifications.disabled', { name: flag.name }),
      alertType: 'success'
    })
  }

  data.togglingFlag = null
}

// Delete flag
const handleDelete = async (flag: FeatureFlagType, event: Event) => {
  event.stopPropagation()
  const key = getFlagKey(flag)
  if (data.deletingFlag) return

  // Confirm deletion
  if (!confirm(t('dashboard.admin.features.confirmDelete', { category: flag.category, name: flag.name }))) {
    return
  }

  data.deletingFlag = key

  const success = await featureFlagsStore.deleteFlag(flag.category, flag.name)

  if (success) {
    notifyStore.createNotify({
      id: uuid(),
      title: t('common.delete'),
      message: t('dashboard.admin.features.notifications.deleted', { category: flag.category, name: flag.name }),
      alertType: 'success'
    })
  }

  data.deletingFlag = null
}

// Expand all categories when new data loads
const expandAllCategories = () => {
  adminCategories.value.forEach((cat) => {
    data.expandedCategories.add(cat)
  })
}

// Expose search and expand controls
defineExpose({
  search: computed({
    get: () => data.search,
    set: (val: string) => { data.search = val }
  }),
  expandAllCategories
})
</script>

<template>
  <div class="feature-table-container">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular color="purple" indeterminate size="40" width="3" />
      <span class="loading-text">{{ $t('dashboard.admin.features.loading') }}</span>
    </div>

    <!-- Empty State -->
    <div v-else-if="adminFlags.length === 0" class="empty-state">
      <div class="empty-icon">
        <v-icon icon="mdi-flag-off-outline" size="48" />
      </div>
      <span class="empty-title">{{ $t('dashboard.admin.features.empty.title') }}</span>
      <span class="empty-subtitle">{{ $t('dashboard.admin.features.empty.subtitle') }}</span>
    </div>

    <!-- No Results State -->
    <div v-else-if="filteredCategories.length === 0" class="empty-state">
      <div class="empty-icon">
        <v-icon icon="mdi-magnify-close" size="48" />
      </div>
      <span class="empty-title">{{ $t('dashboard.admin.features.empty.noResults') }}</span>
      <span class="empty-subtitle">{{ $t('dashboard.admin.features.empty.noResultsQuery', { query: data.search }) }}</span>
    </div>

    <!-- Category Groups -->
    <div v-else class="category-list">
      <div
        v-for="category in filteredCategories"
        :key="category"
        class="category-group"
        :class="{ expanded: isCategoryExpanded(category) }"
      >
        <!-- Category Header -->
        <button class="category-header" @click="toggleCategory(category)">
          <div class="category-left">
            <v-icon
              :icon="isCategoryExpanded(category) ? 'mdi-chevron-down' : 'mdi-chevron-right'"
              size="20"
              class="expand-icon"
            />
            <div class="category-icon">
              <v-icon icon="mdi-folder-outline" size="18" />
            </div>
            <span class="category-name">{{ category }}</span>
          </div>
          <div class="category-right">
            <span class="category-count">{{ $t('dashboard.admin.features.table.categoryFlags', { count: filteredFlagsGrouped[category]?.length ?? 0 }) }}</span>
          </div>
        </button>

        <!-- Category Flags -->
        <div v-if="isCategoryExpanded(category)" class="category-content">
          <div
            v-for="flag in filteredFlagsGrouped[category]"
            :key="getFlagKey(flag)"
            class="flag-row"
          >
            <div class="flag-info">
              <span class="flag-name">{{ flag.name }}</span>
            </div>

            <div class="flag-actions">
              <!-- Toggle Switch -->
              <button
                class="toggle-switch"
                :class="{ enabled: flag.enabled }"
                :disabled="data.togglingFlag === getFlagKey(flag)"
                @click="handleToggle(flag, $event)"
              >
                <span class="toggle-track">
                  <span class="toggle-thumb">
                    <v-progress-circular
                      v-if="data.togglingFlag === getFlagKey(flag)"
                      indeterminate
                      size="12"
                      width="2"
                    />
                  </span>
                </span>
                <span class="toggle-label">{{ flag.enabled ? $t('dashboard.admin.features.table.active') : $t('dashboard.admin.features.table.inactive') }}</span>
              </button>

              <!-- Edit Button -->
              <button class="action-btn edit-btn" :title="$t('dashboard.admin.features.table.edit')" @click.stop="emits('edit', flag)">
                <v-icon icon="mdi-pencil-outline" size="18" />
              </button>

              <!-- Delete Button -->
              <button
                class="action-btn delete-btn"
                :title="$t('dashboard.admin.features.table.delete')"
                :disabled="data.deletingFlag === getFlagKey(flag)"
                @click="handleDelete(flag, $event)"
              >
                <v-progress-circular
                  v-if="data.deletingFlag === getFlagKey(flag)"
                  indeterminate
                  size="14"
                  width="2"
                />
                <v-icon v-else icon="mdi-delete-outline" size="18" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.feature-table-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Loading & Empty States */
.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 300px;
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 16px;
}

.loading-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(156, 39, 176, 0.08);
  border: 1px solid rgba(156, 39, 176, 0.15);
  border-radius: 20px;

  .v-icon {
    color: rgba(186, 104, 200, 0.5);
  }
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

.empty-subtitle {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.5);
}

/* Category Groups */
.category-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.category-group {
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s ease;

  &.expanded {
    border-color: rgba(156, 39, 176, 0.2);
  }
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 18px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
}

.category-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.expand-icon {
  color: rgba(200, 218, 255, 0.5);
  transition: transform 0.2s ease;
}

.category-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(156, 39, 176, 0.1);
  border: 1px solid rgba(156, 39, 176, 0.2);
  border-radius: 8px;

  .v-icon {
    color: #ba68c8;
  }
}

.category-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.category-right {
  display: flex;
  align-items: center;
}

.category-count {
  padding: 4px 10px;
  background: rgba(156, 39, 176, 0.1);
  border: 1px solid rgba(156, 39, 176, 0.15);
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #ba68c8;
}

/* Category Content */
.category-content {
  border-top: 1px solid rgba(120, 163, 255, 0.08);
}

.flag-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px 12px 62px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  transition: background 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
}

.flag-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.flag-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
}

.flag-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Toggle Switch */
.toggle-switch {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  background: rgba(244, 67, 54, 0.08);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;

  &.enabled {
    background: rgba(76, 175, 80, 0.08);
    border-color: rgba(76, 175, 80, 0.2);

    .toggle-track {
      background: rgba(76, 175, 80, 0.3);
    }

    .toggle-thumb {
      transform: translateX(16px);
      background: #4caf50;
    }

    .toggle-label {
      color: #81c784;
    }
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
}

.toggle-track {
  position: relative;
  width: 36px;
  height: 20px;
  background: rgba(244, 67, 54, 0.3);
  border-radius: 10px;
  transition: background 0.2s ease;
}

.toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: #f44336;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-label {
  font-size: 0.8rem;
  font-weight: 500;
  color: #e57373;
  min-width: 45px;
}

/* Action Buttons */
.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &.edit-btn {
    color: rgba(200, 218, 255, 0.4);

    &:hover {
      background: rgba(156, 39, 176, 0.1);
      border-color: rgba(156, 39, 176, 0.25);
      color: #ba68c8;
    }
  }

  &.delete-btn {
    color: rgba(200, 218, 255, 0.4);

    &:hover {
      background: rgba(244, 67, 54, 0.1);
      border-color: rgba(244, 67, 54, 0.25);
      color: #f44336;
    }

    &:disabled {
      opacity: 0.7;
      cursor: wait;
    }
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .category-group {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .category-name,
  .flag-name {
    color: rgba(15, 23, 42, 0.9);
  }

  .loading-container,
  .empty-state {
    background: rgba(255, 255, 255, 0.7);
  }
}

@media (max-width: 768px) {
  .flag-row {
    padding-left: 18px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .flag-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (prefers-reduced-motion: reduce) {
  .category-group,
  .category-header,
  .flag-row,
  .toggle-switch,
  .toggle-track,
  .toggle-thumb,
  .action-btn {
    transition: none;
  }
}
</style>
